import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuthStore } from '@/store/auth.store'
import { billsService } from '@/services/billsService'
import { vendorsService } from '@/services/vendorsService'
import { branchService } from '@/services/branchService'
import { projectsService } from '@/services/projectsService'
import type { BillVm, BranchEntity, Vendor } from '@/api/types'
import { Button } from '@/components/ui/button'
import { formatCalendarDate } from '@/lib/calendarDate'
import {
  formatDocNo,
  PurchasePrintLayout,
  type PrintLine,
  type PrintParty,
} from '@/modules/purchases/pages/PurchasePrintLayout'

function vendorParty(vendor: Vendor | null, cityName: string, countryName: string): PrintParty {
  const v = vendor as Vendor & Record<string, any> | null
  return {
    nameEn: v?.vendorNameEn || v?.vendorName || '-',
    nameAr: v?.vendorNameAr,
    address: v?.address,
    country: countryName || 'Saudi Arabia (SA)',
    city: cityName || '-',
    zip: v?.zipCode,
    phone: v?.phoneNo || v?.mobileNo,
    vat: v?.gstNo || v?.vatNo || v?.vatNumber,
    otherId: v?.companyRegistrationNo,
  }
}

function branchParty(branch: BranchEntity | null, cityName: string): PrintParty {
  return {
    nameEn: branch?.nameEn || '-',
    nameAr: branch?.nameAr,
    address: [branch?.addressEn, branch?.street].filter(Boolean).join(', '),
    addressAr: branch?.addressAr,
    country: branch?.countryName || 'Saudi Arabia (SA)',
    city: cityName || branch?.cityName || '-',
    zip: branch?.zipCode,
    phone: branch?.phone,
    vat: branch?.vatNo,
    otherId: branch?.crNo,
  }
}

export default function BillsPrint() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [bill, setBill] = useState<BillVm | null>(null)
  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [projectName, setProjectName] = useState('-')
  const [vendorCity, setVendorCity] = useState('')
  const [vendorCountry, setVendorCountry] = useState('')
  const [branchCity, setBranchCity] = useState('')

  useEffect(() => {
    if (!branchId || !id) {
      setError('Missing branch or bill identifier')
      setLoading(false)
      return
    }
    const billId = Number(id)
    if (Number.isNaN(billId)) {
      setError('Invalid bill id')
      setLoading(false)
      return
    }

    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const billData = await billsService.getBillById(branchId, billId)
        setBill(billData)
        const [vendorData, branchData] = await Promise.all([
          billData.vendorId ? vendorsService.getVendorById(branchId, billData.vendorId).catch(() => null) : null,
          branchService.getBranchById(branchId).catch(() => null),
        ])
        setVendor(vendorData)
        setBranch(branchData)

        if (billData.projectId && Number(billData.projectId) > 0) {
          try {
            const project = await projectsService.getProjectById(Number(billData.projectId), branchId)
            setProjectName(project.name || project.projectName || project.projectNameEn || project.code || '-')
          } catch {
            setProjectName('-')
          }
        }

        if (branchData?.country && branchData?.city) {
          try {
            const cities = await branchService.getCityLookups(branchData.country)
            const city = cities.find((c: any) => c.id === branchData.city)
            if (city) setBranchCity(city.lookupNameEn || '')
          } catch {
            /* ignore */
          }
        }

        if (vendorData?.country) {
          try {
            const [countries, cities] = await Promise.all([
              vendorsService.getCountries(),
              vendorsService.getCities(vendorData.country),
            ])
            const country = countries.find((c) => c.id === vendorData.country)
            const city = cities.find((c) => c.id === vendorData.city)
            setVendorCountry(country?.lookupNameEn || '')
            setVendorCity(city?.lookupNameEn || '')
          } catch {
            /* ignore */
          }
        }
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Unable to load bill details'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [branchId, id])

  const lines: PrintLine[] = useMemo(() => {
    return (bill?.billLines || []).map((line) => ({
      description: line.description,
      qty: line.qty,
      unitPrice: line.unitPrice,
      itemPrice: line.itemPrice,
      taxableAmount: line.taxableAmount || line.itemPrice,
      taxRate: line.taxRate || line.taxPercent,
      taxAmount: line.taxAmount,
      lineAmount: line.lineAmount,
    }))
  }, [bill])

  const totals = useMemo(() => {
    const subTotal = lines.reduce((sum, line) => sum + (line.itemPrice ?? 0), 0)
    const taxableAmount = lines.reduce((sum, line) => sum + (line.taxableAmount ?? line.itemPrice ?? 0), 0)
    const vatAmount = lines.reduce((sum, line) => sum + (line.taxAmount ?? 0), 0)
    return {
      subTotal: bill?.subTotal || subTotal,
      taxableAmount,
      vatAmount: bill?.totalTax || vatAmount,
      total: bill?.total || taxableAmount + vatAmount,
      retentionPercent: bill?.retentionPercent || 0,
    }
  }, [bill, lines])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing bill for print...</p>
        </div>
      </div>
    )
  }

  if (error || !bill) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Bill not found'}</p>
        <Button onClick={() => navigate('/purchases/bills')}>Back to Bills</Button>
      </div>
    )
  }

  return (
    <PurchasePrintLayout
      titleAr="فاتورة مشتريات"
      titleEn="Purchase Bill"
      printButtonLabel="Print Bill"
      infoRows={[
        { en: 'Bill #:', ar: 'رقم الفاتورة', value: formatDocNo('BILL', bill.billNo || bill.billId) },
        { en: 'Bill Date:', ar: 'تاريخ إصدار الفاتورة', value: formatCalendarDate(bill.billDate) },
        { en: 'Supply Date:', ar: 'تاريخ التوريد', value: formatCalendarDate(bill.vendorBillDate) || '-' },
        { en: 'Due Date:', ar: 'تاريخ الاستحقاق', value: formatCalendarDate(bill.dueDate) || '-' },
        { en: 'Project Name:', ar: 'اسم المشروع', value: projectName },
      ]}
      seller={vendorParty(vendor, vendorCity, vendorCountry)}
      buyer={branchParty(branch, branchCity)}
      lines={lines}
      totals={totals}
      notes={bill.note || bill.refNo}
      footer="This bill was generated electronically and does not require any signature. | تم إنشاء هذه الفاتورة إلكترونيًا ولا تحتاج إلى توقيع."
    />
  )
}
